import { createFileRoute, useParams } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Download, PackageCheck, PackageOpen } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { EmptyState } from '@/components/ui/empty-state';
import {
  useArchiveListQuery,
  useArchiveQuery,
  useCreateArchiveMutation,
  useDownloadArchiveMutation,
  useHandoffArchiveMutation,
} from '@/lib/api/archive';
import { formatDateTime, titleCase } from '@/lib/utils/format';

/**
 * Archive tab. Three actions:
 *
 *   1. **Kick a build** — POST /archive; the API responds 202 with
 *      `archive_id` + `build_status=building`. We immediately switch the
 *      detail panel to that archive and start polling.
 *   2. **Poll status** — GET /archive/:id every 2 seconds while
 *      `build_status === 'building'`; the polling stops automatically
 *      once the row resolves to succeeded / failed.
 *   3. **Handoff** — POST /:archive_id/handoff once the build succeeds.
 */
export const Route = createFileRoute('/engagements/$engagementId/archive')({
  component: ArchiveTab,
});

function ArchiveTab() {
  const { engagementId } = useParams({ from: '/engagements/$engagementId/archive' });
  const list = useArchiveListQuery(engagementId);
  const create = useCreateArchiveMutation(engagementId);
  const [activeArchiveId, setActiveArchiveId] = useState<string | null>(null);
  const detail = useArchiveQuery(engagementId, activeArchiveId, {
    // Poll while the archive is still building.
    pollMs: activeArchiveId ? 2000 : undefined,
  });
  const handoff = useHandoffArchiveMutation(engagementId, activeArchiveId);
  const download = useDownloadArchiveMutation();

  async function startBuild() {
    const r = await create.mutateAsync();
    setActiveArchiveId(r.archive_id);
  }

  async function submitHandoff(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const recipient = String(form.get('recipient') ?? '').trim();
    if (!recipient) return;
    await handoff.mutateAsync({ recipient, delivery_method: 'signed_link' });
  }

  // Stop polling once the row is terminal.
  const archive = detail.data;
  const isBuilding = archive?.build_status === 'building';

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Engagement closeout</CardTitle>
          <CardDescription>
            Build a deterministic archive of the engagement and record the handoff to the client.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-3">
          <Button onClick={startBuild} disabled={create.isPending}>
            {create.isPending ? (
              <Spinner />
            ) : (
              <PackageOpen className="h-4 w-4" aria-hidden="true" />
            )}
            <span>Build a new archive</span>
          </Button>
          {create.error ? <ErrorMessage error={create.error} /> : null}
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Archives</CardTitle>
            <CardDescription>Click an archive to view its status + handoff state.</CardDescription>
          </CardHeader>
          <CardContent>
            {list.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Spinner /> Loading archives…
              </div>
            ) : list.error ? (
              <ErrorMessage error={list.error} />
            ) : !list.data || list.data.items.length === 0 ? (
              <EmptyState title="No archives yet" description="Kick a build to get started." />
            ) : (
              <ul className="divide-y rounded-md border">
                {list.data.items.map((row) => (
                  <li key={row.archive_id}>
                    <button
                      type="button"
                      onClick={() => setActiveArchiveId(row.archive_id)}
                      className={
                        activeArchiveId === row.archive_id
                          ? 'flex w-full items-center justify-between gap-2 bg-accent p-3 text-left text-sm'
                          : 'flex w-full items-center justify-between gap-2 p-3 text-left text-sm hover:bg-accent'
                      }
                    >
                      <span className="font-mono text-xs">{row.archive_id.slice(0, 12)}…</span>
                      <Badge
                        variant={
                          row.build_status === 'succeeded'
                            ? 'success'
                            : row.build_status === 'failed'
                              ? 'destructive'
                              : 'secondary'
                        }
                      >
                        {titleCase(row.build_status)}
                      </Badge>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Detail</CardTitle>
            <CardDescription>
              {activeArchiveId
                ? 'Polling while the build is in flight.'
                : 'Pick an archive on the left.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {!activeArchiveId ? (
              <p className="text-muted-foreground">No archive selected.</p>
            ) : detail.isLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Spinner /> Loading…
              </div>
            ) : detail.error ? (
              <ErrorMessage error={detail.error} />
            ) : archive ? (
              <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-2 text-xs">
                <dt className="text-muted-foreground">archive_id</dt>
                <dd className="font-mono">{archive.archive_id}</dd>
                <dt className="text-muted-foreground">status</dt>
                <dd>
                  <Badge
                    variant={
                      archive.build_status === 'succeeded'
                        ? 'success'
                        : archive.build_status === 'failed'
                          ? 'destructive'
                          : 'secondary'
                    }
                  >
                    {titleCase(archive.build_status)}
                  </Badge>
                  {isBuilding ? (
                    <span className="ml-2 inline-flex items-center gap-1 text-muted-foreground">
                      <Spinner />
                      <span>Polling…</span>
                    </span>
                  ) : null}
                </dd>
                <dt className="text-muted-foreground">built_at</dt>
                <dd>{formatDateTime(archive.built_at)}</dd>
                <dt className="text-muted-foreground">handed_off_at</dt>
                <dd>{formatDateTime(archive.handed_off_at)}</dd>
                {archive.build_error ? (
                  <>
                    <dt className="text-muted-foreground">error</dt>
                    <dd className="text-destructive">{archive.build_error}</dd>
                  </>
                ) : null}
              </dl>
            ) : null}

            {archive?.build_status === 'succeeded' && archive.download_url ? (
              <>
                <Button
                  variant="outline"
                  className="w-full"
                  disabled={download.isPending}
                  onClick={() =>
                    download.mutate({
                      archive_id: archive.archive_id,
                      download_url: archive.download_url!,
                    })
                  }
                >
                  {download.isPending ? (
                    <Spinner />
                  ) : (
                    <Download className="h-4 w-4" aria-hidden="true" />
                  )}
                  <span>Download archive</span>
                </Button>
                {download.error ? <ErrorMessage error={download.error} /> : null}
              </>
            ) : null}

            {archive?.build_status === 'succeeded' && !archive.handed_off_at ? (
              <form className="space-y-2 border-t pt-3" onSubmit={submitHandoff}>
                <Label htmlFor="recipient">Handoff recipient</Label>
                <Input id="recipient" name="recipient" placeholder="recipient@example.com" />
                <Button
                  type="submit"
                  variant="default"
                  className="w-full"
                  disabled={handoff.isPending}
                >
                  {handoff.isPending ? (
                    <Spinner />
                  ) : (
                    <PackageCheck className="h-4 w-4" aria-hidden="true" />
                  )}
                  <span>Record handoff</span>
                </Button>
                {handoff.error ? <ErrorMessage error={handoff.error} /> : null}
              </form>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
